import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Headset, Plus, Loader2, MessageSquare, Send, TicketCheck } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { useEffect, useRef } from "react";

const categoryLabels: Record<string, string> = {
  general: "عام",
  login: "تسجيل الدخول",
  upload: "رفع الملفات",
  live: "البث المباشر",
  billing: "الفواتير والدفع",
  technical: "مشكلة تقنية",
};

const statusLabels: Record<string, string> = {
  open: "مفتوحة",
  in_progress: "قيد المعالجة",
  resolved: "تم الحل",
  closed: "مغلقة",
};

const statusColors: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  in_progress: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  resolved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  closed: "bg-muted text-muted-foreground",
};

const SupportPage = () => {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [replyText, setReplyText] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Form state
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [priority, setPriority] = useState("medium");
  const [creating, setCreating] = useState(false);

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["my-support-tickets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("*")
        .eq("created_by", user?.id || "")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  const { data: replies } = useQuery({
    queryKey: ["ticket-replies", selectedTicket?.id],
    queryFn: async () => {
      if (!selectedTicket?.id) return [];
      const { data, error } = await supabase
        .from("ticket_replies")
        .select("*")
        .eq("ticket_id", selectedTicket.id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedTicket?.id,
  });

  useEffect(() => {
    if (!selectedTicket?.id) return;
    const channel = supabase
      .channel(`user-ticket-${selectedTicket.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "ticket_replies",
        filter: `ticket_id=eq.${selectedTicket.id}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ["ticket-replies", selectedTicket.id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedTicket?.id, queryClient]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [replies]);

  const handleCreate = async () => {
    if (!subject.trim() || !description.trim() || !user?.id) return;
    setCreating(true);
    try {
      const { error } = await supabase.from("support_tickets").insert({
        created_by: user.id,
        nursery_id: profile?.nursery_id || null,
        subject: subject.trim(),
        description: description.trim(),
        category,
        priority,
      });
      if (error) throw error;
      toast.success("تم إرسال التذكرة بنجاح! سيتواصل معك فريق الدعم قريباً.");
      setCreateOpen(false);
      setSubject("");
      setDescription("");
      setCategory("general");
      setPriority("medium");
      queryClient.invalidateQueries({ queryKey: ["my-support-tickets"] });
    } catch {
      toast.error("حدث خطأ في إرسال التذكرة");
    } finally {
      setCreating(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedTicket?.id || !user?.id) return;
    const { error } = await supabase.from("ticket_replies").insert({
      ticket_id: selectedTicket.id,
      sender_id: user.id,
      sender_name: profile?.full_name || "مستخدم",
      sender_role: "user",
      content: replyText.trim(),
    });
    if (error) { toast.error("خطأ في إرسال الرد"); return; }
    setReplyText("");
    queryClient.invalidateQueries({ queryKey: ["ticket-replies", selectedTicket.id] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Headset className="w-6 h-6 text-primary" />
            الدعم الفني
          </h1>
          <p className="text-muted-foreground text-sm mt-1">أرسل استفساراتك ومشاكلك وسنرد عليك في أقرب وقت</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              تذكرة جديدة
            </Button>
          </DialogTrigger>
          <DialogContent dir="rtl" className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Headset className="w-5 h-5 text-primary" />
                تذكرة دعم جديدة
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>الموضوع *</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="ملخص المشكلة أو الاستفسار" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>التصنيف</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(categoryLabels).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>الأولوية</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">منخفضة</SelectItem>
                      <SelectItem value="medium">متوسطة</SelectItem>
                      <SelectItem value="high">عالية</SelectItem>
                      <SelectItem value="urgent">عاجلة</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>الوصف التفصيلي *</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="اشرح المشكلة بالتفصيل..."
                  rows={4}
                />
              </div>
              <Button onClick={handleCreate} disabled={creating || !subject.trim() || !description.trim()} className="w-full">
                {creating ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Send className="w-4 h-4 ml-2" />}
                إرسال التذكرة
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tickets List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">تذاكري ({tickets?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>
          ) : !tickets?.length ? (
            <div className="text-center py-12">
              <TicketCheck className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">لا توجد تذاكر. اضغط "تذكرة جديدة" للتواصل مع الدعم الفني.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>الموضوع</TableHead>
                  <TableHead>التصنيف</TableHead>
                  <TableHead>الحالة</TableHead>
                  <TableHead>التاريخ</TableHead>
                  <TableHead>إجراء</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((t: any) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono text-xs">#{t.ticket_number}</TableCell>
                    <TableCell className="font-medium">{t.subject}</TableCell>
                    <TableCell className="text-xs">{categoryLabels[t.category] || t.category}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[t.status]}`}>
                        {statusLabels[t.status]}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {format(new Date(t.created_at), "dd MMM HH:mm", { locale: ar })}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedTicket(t)}>
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Chat Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(o) => !o && setSelectedTicket(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="w-4 h-4 text-primary" />
              تذكرة #{selectedTicket?.ticket_number} - {selectedTicket?.subject}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 border rounded-lg flex flex-col">
            <div className="flex-1 overflow-y-auto p-3 space-y-3 max-h-[350px]">
              {/* Original description */}
              <div className="flex justify-end">
                <div className="max-w-[75%] rounded-lg p-3 text-sm bg-muted">
                  <p className="text-xs text-muted-foreground mb-1">الوصف الأصلي</p>
                  <p className="whitespace-pre-wrap">{selectedTicket?.description}</p>
                </div>
              </div>
              {replies?.map((r: any) => (
                <div key={r.id} className={`flex ${r.sender_role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-lg p-3 text-sm ${
                    r.sender_role === "user" ? "bg-muted" : "bg-primary/10"
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-xs">{r.sender_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(r.created_at), "HH:mm", { locale: ar })}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap">{r.content}</p>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            {selectedTicket?.status !== "closed" && (
              <div className="border-t p-3 flex gap-2">
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="اكتب رسالتك..."
                  className="resize-none min-h-[44px] max-h-[80px]"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }
                  }}
                />
                <Button size="icon" onClick={sendReply} disabled={!replyText.trim()}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportPage;
